import React, { useState, useEffect } from 'react';
import api from '../api';

// TypeScript interfaces for taxonomy entities
export interface Department {
  id: number;
  name: string;
  code: string;
  abbreviation: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: number;
  name: string;
  code?: string;
  abbreviation: string;
  description?: string;
  department_id: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SubCategory {
  id: number;
  name: string;
  code?: string;
  abbreviation: string;
  description?: string;
  category_id: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface TaxonomySelectorProps {
  departmentId?: number;
  categoryId?: number;
  subcategoryId?: number;
  onDepartmentChange: (departmentId: number | undefined) => void;
  onCategoryChange: (categoryId: number | undefined) => void;
  onSubcategoryChange: (subcategoryId: number | undefined) => void;
  required?: boolean;
  disabled?: boolean;
}

const TaxonomySelector: React.FC<TaxonomySelectorProps> = ({
  departmentId,
  categoryId,
  subcategoryId,
  onDepartmentChange,
  onCategoryChange,
  onSubcategoryChange,
  required = false,
  disabled = false,
}) => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [loading, setLoading] = useState({
    departments: false,
    categories: false,
    subcategories: false,
  });
  const [error, setError] = useState<string | null>(null);

  // Load departments on component mount
  useEffect(() => {
    fetchDepartments();
  }, []);

  // Load categories when department is selected
  useEffect(() => {
    if (departmentId) {
      fetchCategories(departmentId);
    } else {
      setCategories([]);
      setSubcategories([]);
    }
  }, [departmentId]);

  // Load subcategories when category is selected
  useEffect(() => {
    if (categoryId) {
      fetchSubcategories(categoryId);
    } else {
      setSubcategories([]);
    }
  }, [categoryId]);

  const fetchDepartments = async () => {
    setLoading((prev) => ({ ...prev, departments: true }));
    setError(null);
    try {
      console.log('[TaxonomySelector] Fetching departments...');
      const response = await api.get('/api/taxonomy/departments', {
        params: { active_only: true },
      });
      console.log('[TaxonomySelector] Departments response:', response.data);
      console.log('[TaxonomySelector] Number of departments:', response.data.length);
      setDepartments(response.data);
    } catch (err: any) {
      console.error('[TaxonomySelector] Error fetching departments:', err);
      console.error('[TaxonomySelector] Error response:', err.response);
      setError('Failed to load departments');
    } finally {
      setLoading((prev) => ({ ...prev, departments: false }));
    }
  };

  const fetchCategories = async (deptId: number) => {
    setLoading((prev) => ({ ...prev, categories: true }));
    setError(null);
    try {
      const response = await api.get('/api/taxonomy/categories', {
        params: { department_id: deptId, active_only: true },
      });
      setCategories(response.data);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError('Failed to load categories');
      setCategories([]);
    } finally {
      setLoading((prev) => ({ ...prev, categories: false }));
    }
  };

  const fetchSubcategories = async (catId: number) => {
    setLoading((prev) => ({ ...prev, subcategories: true }));
    setError(null);
    try {
      const response = await api.get('/api/taxonomy/subcategories', {
        params: { category_id: catId, active_only: true },
      });
      setSubcategories(response.data);
    } catch (err: any) {
      console.error('Error fetching subcategories:', err);
      setError('Failed to load subcategories');
      setSubcategories([]);
    } finally {
      setLoading((prev) => ({ ...prev, subcategories: false }));
    }
  };

  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    console.log('[TaxonomySelector] Department changed, value:', value);
    const newDepartmentId = value ? parseInt(value, 10) : undefined;
    console.log('[TaxonomySelector] Parsed department ID:', newDepartmentId);
    
    // Clear dependent selections when parent changes
    onDepartmentChange(newDepartmentId);
    onCategoryChange(undefined);
    onSubcategoryChange(undefined);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const newCategoryId = value ? parseInt(value, 10) : undefined;
    
    // Clear dependent selections when parent changes
    onCategoryChange(newCategoryId);
    onSubcategoryChange(undefined);
  };

  const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const newSubcategoryId = value ? parseInt(value, 10) : undefined;
    onSubcategoryChange(newSubcategoryId);
  };

  return (
    <div className="taxonomy-selector">
      {error && (
        <div style={{ 
          padding: '0.75rem', 
          marginBottom: '1rem', 
          backgroundColor: '#fee2e2', 
          color: '#991b1b',
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Department Dropdown */}
      <div className="form-group">
        <label className="form-label">
          Department {required && <span style={{ color: 'red' }}>*</span>}
        </label>
        <select
          className="form-input"
          value={departmentId || ''}
          onChange={handleDepartmentChange}
          disabled={disabled || loading.departments}
          required={required}
        >
          <option value="">
            {loading.departments ? 'Loading departments...' : 'Select Department'}
          </option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>
              {dept.name}
            </option>
          ))}
        </select>
        {/* Debug info */}
        <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
          Debug: {departments.length} departments loaded, 
          disabled={String(disabled || loading.departments)}, 
          selected={departmentId || 'none'}
        </div>
      </div>

      {/* Category Dropdown - Disabled until department selected */}
      <div className="form-group">
        <label className="form-label">
          Category {required && <span style={{ color: 'red' }}>*</span>}
        </label>
        <select
          className="form-input"
          value={categoryId || ''}
          onChange={handleCategoryChange}
          disabled={disabled || !departmentId || loading.categories}
          required={required}
        >
          <option value="">
            {!departmentId
              ? 'Select department first'
              : loading.categories
              ? 'Loading categories...'
              : 'Select Category'}
          </option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* Subcategory Dropdown - Disabled until category selected */}
      <div className="form-group">
        <label className="form-label">
          Subcategory
        </label>
        <select
          className="form-input"
          value={subcategoryId || ''}
          onChange={handleSubcategoryChange}
          disabled={disabled || !categoryId || loading.subcategories}
        >
          <option value="">
            {!categoryId
              ? 'Select category first'
              : loading.subcategories
              ? 'Loading subcategories...'
              : 'Select Subcategory (optional)'}
          </option>
          {subcategories.map((subcat) => (
            <option key={subcat.id} value={subcat.id}>
              {subcat.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default TaxonomySelector;
